/**
 * Delta merge for `WebInitCache` responses.
 *
 * Protocol: the server returns ONLY rows whose per-row timestamp moved past
 * what the client advertised (cache.TS). Unchanged sections arrive as empty
 * arrays. New / updated rows must be upserted into the existing collection
 * by their primary key — replacing the whole array with the delta wipes
 * legitimate rows that weren't part of this round.
 *
 * Primary keys per section mirror WebAdmin2's IndexedDB `keyPath` values
 * (see WebAdmin2/basic/storage.ts) so both admin clients stay in sync with
 * the server's delta semantics.
 *
 * Unknown collections fall back to "replace if non-empty" — safe-ish for
 * arrays we don't yet recognise; add them to the map as they surface.
 */
import type { InitCache } from "../types/session.ts";

type Obj = Record<string, unknown>;

/** Map of `dot.path` → primary-key field name for each known array section. */
const SECTION_KEYS: Record<string, string> = {
  // Top-level arrays
  ColumnInfo: "ColumnName",
  Promotions: "PromotionID",
  Permissions: "RowID",
  TimeZones: "CombinedID",
  ServerSettings: "Key",
  TicketStatuses: "ID",
  TicketIssueTypes: "ID",
  IssueCategories: "ID",
  UserApps: "AppName",
  ApprovalKioskTests: "ID",

  // PageInfo sub-arrays
  "PageInfo.Page": "RowID",
  "PageInfo.PageInfo": "ID",
  "PageInfo.MenuItem": "ID",
  "PageInfo.Data": "RowID",
  "PageInfo.Pivot": "ID",
  "PageInfo.Hierarchy": "ID",

  // CommonData sub-arrays
  "CommonData.Kiosk": "KioskID",
  "CommonData.Location": "LocationID",
  "CommonData.Common": "TypeName",
  "CommonData.Product": "ProductID",
  "CommonData.PriceType": "PriceTypeID",
  "CommonData.PriceCategory": "CategoryID",
  "CommonData.CsIssue": "CustomerIssueID",
  "CommonData.CsIssueType": "CustomerIssueTypeID",
  "CommonData.IngressGroup": "ID",

  // MovieData sub-arrays
  "MovieData.Persons": "PersonID",
  "MovieData.ProductFormats": "ID",
  "MovieData.MovieCompany": "MovieCompanyID",
  "MovieData.DealType": "DealTypeID",
  "MovieData.Region": "RegionID",
  "MovieData.Genre": "GenreID",
  "MovieData.Sound": "SoundSystemID",
  "MovieData.Subtitle": "SubtitleID",
  "MovieData.Language": "LanguageID",
};

function upsertByKey(prev: Obj[], next: Obj[], key: string): Obj[] {
  if (next.length === 0) return prev;
  if (prev.length === 0) return next;
  const merged = prev.slice();
  const pos = new Map<unknown, number>();
  for (let i = 0; i < merged.length; i++) {
    pos.set((merged[i] as Obj)[key], i);
  }
  for (const row of next) {
    if (!row || typeof row !== "object") continue;
    const k = (row as Obj)[key];
    const existing = pos.get(k);
    if (existing !== undefined) {
      merged[existing] = row;
    } else {
      pos.set(k, merged.length);
      merged.push(row);
    }
  }
  return merged;
}

function mergeArraySection(prev: unknown, next: unknown, key: string): unknown {
  if (!Array.isArray(next)) return next !== undefined ? next : prev;
  if (!Array.isArray(prev)) return next;
  return upsertByKey(prev as Obj[], next as Obj[], key);
}

function mergeNested(prevParent: Obj, nextParent: Obj, pathPrefix: string): Obj {
  const out: Obj = { ...nextParent };
  for (const childKey of Object.keys(nextParent)) {
    const mapKey = SECTION_KEYS[`${pathPrefix}.${childKey}`];
    if (mapKey) {
      out[childKey] = mergeArraySection(prevParent[childKey], nextParent[childKey], mapKey);
    }
  }
  // Bring forward children the server omitted entirely this round.
  for (const childKey of Object.keys(prevParent)) {
    if (!(childKey in out)) {
      out[childKey] = prevParent[childKey];
    }
  }
  return out;
}

/**
 * Parse PageInfo.Data[*].MigratedFilters from the server's raw
 * "oldCol>newCol,..." string into the runtime lookup object
 * `{ "NEWCOL": "oldCol", "OLDCOL": null }`. In-place on `next` so
 * freshly-delivered rows get parsed once before they land in the merged
 * cache. Rows already in prev stay parsed — they aren't touched here.
 */
export function parseMigratedFilters(cache: InitCache): void {
  const data = cache.PageInfo?.Data;
  if (!Array.isArray(data)) return;
  for (const row of data) {
    const raw = row.MigratedFilters as unknown;
    if (!raw || typeof raw !== "string") continue;
    const parsed: Record<string, string | null> = {};
    for (const entry of raw.split(",")) {
      const parts = entry.split(">", 2);
      if (parts.length === 2 && parts[0] && parts[1]) {
        parsed[parts[1].toUpperCase()] = parts[0];
        parsed[parts[0].toUpperCase()] = null;
      }
    }
    (row as unknown as Obj).MigratedFilters = parsed;
  }
}

/**
 * Apply a delta `next` on top of `prev`. When `prev` is null this is the
 * first load — `next` is assumed to be the full cache and returned as-is
 * after parsing its rows.
 */
export function mergeCacheDelta(prev: InitCache | null, next: InitCache): InitCache {
  parseMigratedFilters(next);
  if (!prev) return next;

  const prevO = prev as unknown as Obj;
  const out: Obj = { ...(next as unknown as Obj) };

  for (const [path, key] of Object.entries(SECTION_KEYS)) {
    if (path.includes(".")) continue;
    if (path in out) {
      out[path] = mergeArraySection(prevO[path], out[path], key);
    }
  }

  if (out.PageInfo && typeof out.PageInfo === "object") {
    out.PageInfo = mergeNested(
      (prevO.PageInfo as Obj | undefined) ?? {},
      out.PageInfo as Obj,
      "PageInfo",
    );
  } else if (prevO.PageInfo && !out.PageInfo) {
    out.PageInfo = prevO.PageInfo;
  }
  if (out.CommonData && typeof out.CommonData === "object") {
    out.CommonData = mergeNested(
      (prevO.CommonData as Obj | undefined) ?? {},
      out.CommonData as Obj,
      "CommonData",
    );
  } else if (prevO.CommonData && !out.CommonData) {
    out.CommonData = prevO.CommonData;
  }
  if (out.MovieData && typeof out.MovieData === "object") {
    out.MovieData = mergeNested(
      (prevO.MovieData as Obj | undefined) ?? {},
      out.MovieData as Obj,
      "MovieData",
    );
  } else if (prevO.MovieData && !out.MovieData) {
    out.MovieData = prevO.MovieData;
  }

  // Bring forward any top-level field the server omitted this round
  // (scalars, UserInfo, metadata). `undefined` guards fields the server
  // sends as explicit null — those should replace prev.
  for (const k of Object.keys(prevO)) {
    if (!(k in out) || out[k] === undefined) {
      out[k] = prevO[k];
    }
  }

  return out as unknown as InitCache;
}
